import { Routes } from "@angular/router";
import { ApplicationContextResolver } from "app/application-context-resolver.service";
import { HomeGuard } from "app/home-guard.service";
import { WorkspaceViewResolver } from "app/workspace-view-resolver.service";
import { EntryPageTabsResolver } from "dashboard/description/entry-page-tabs-resolver";
import { TaskDetailsResolver } from "dashboard/to-do-list/task-details-resolver.service";
import { DownloadCanActivateGuard } from "files/download-after-authentication/download.can-activate.guard";
import { TodoResolver } from "dashboard/to-do-list/to-do-resolver.service";
import { ExternalLinkRedirectGuard } from "app/external-link-redirect-guard.service";
import { FILES_ROUTES } from "files/files-routes";
import { NewDialogComponent } from "common/newdialog/dialog.component";
import { TitleResolver } from "common/title-resolver.service";
import { PopupComponent } from "common/newdialog/popup.component";
import { DashboardComponent } from "dashboard/dashboard.component";
import { MainComponent } from "app/main.component";
import { ProxyComponent } from "common/proxy/proxy.component";
import { UnavailableComponent } from "common/exception/unavailable.component";
import { ErrorMessageComponent } from "common/exception/error-message.component";
import { ErrorReportComponent } from "common/exception/error-report.component";
import { ErrorComponent } from "common/exception/error.component";
import { GlobalSearchResultsComponent } from "search/results.component";
import { AuthenticationComponent } from "common/authentication/authentication.component";
import { TaskDetailsComponent } from "dashboard/to-do-list/task-details.component";
import { TaskProcessDiffRedirectComponent } from "dashboard/to-do-list/task-process-diff-redirect.component";

export const APP_ROUTES: Routes = [
	{
		path: "login",
		component: AuthenticationComponent,
		data: { titleKey: "main.title" },
	},
	{
		path: "download",
		component: ProxyComponent, // dummy component, because route will be changed in DownloadGuard
		canActivate: [DownloadCanActivateGuard],
	},
	{
		path: "extlink/task/:taskId/todo",
		component: ProxyComponent, // dummy component, because route will be changed in ExternalLinkRedirectGuard
		data: { action: "task" },
		canActivate: [ExternalLinkRedirectGuard],
	},
	{
		path: "extlink/task/:taskId/diff",
		component: ProxyComponent, // dummy component, because route will be changed in ExternalLinkRedirectGuard
		data: { action: "taskdiff" },
		canActivate: [ExternalLinkRedirectGuard],
	},
	{
		path: "extlink/administration/cleanup/archives",
		component: ProxyComponent, // dummy component, because route will be changed in ExternalLinkRedirectGuard
		data: { action: "archives" },
		canActivate: [ExternalLinkRedirectGuard],
	},
	{
		path: "extlink/administration/ai/prompts/:promptId",
		component: ProxyComponent, // dummy component, because route will be changed in ExternalLinkRedirectGuard
		data: { action: "prompt" },
		canActivate: [ExternalLinkRedirectGuard],
	},
	{
		path: "extlink/management/versions/list",
		component: ProxyComponent, // dummy component, because route will be changed in ExternalLinkRedirectGuard
		data: { action: "versionmanagement" },
		canActivate: [ExternalLinkRedirectGuard],
	},
	{
		path: "workspace/:workspaceId/:processVersion",
		resolve: {
			workspaceView: WorkspaceViewResolver,
			applicationContext: ApplicationContextResolver,
		},
		component: MainComponent,
		data: { popupContainer: true },
		children: [
			{
				path: "home",
				component: DashboardComponent,
				resolve: {
					entryPageTabs: EntryPageTabsResolver,
					title: TitleResolver,
				},
				data: { popupContainer: false },
			},
			{
				path: "process",
				loadChildren: () => import("process/process-routes").then((r) => r.PROCESS_ROUTES),
				data: { popupContainer: false },
			},
			{
				path: "process",
				outlet: "popup",
				component: PopupComponent,
				loadChildren: () => import("process/process-routes").then((r) => r.PROCESS_ROUTES),
				data: { popupContainer: false },
			},
			{
				path: "process",
				outlet: "dialog",
				loadChildren: () => import("process/process-routes").then((r) => r.PROCESS_ROUTES),
				data: { popupContainer: false },
			},
			{
				path: "management",
				loadChildren: () => import("management/management-routes").then((r) => r.MANAGEMENT_ROUTES),
				data: { popupContainer: false },
				providers: [PopupComponent, NewDialogComponent],
			},
			{
				path: "management",
				outlet: "popup",
				component: PopupComponent,
				loadChildren: () => import("management/management-routes").then((r) => r.MANAGEMENT_ROUTES),
				data: { popupContainer: false },
				providers: [PopupComponent, NewDialogComponent],
			},
			{
				path: "reporting",
				loadChildren: () => import("reports/reporting-routes").then((r) => r.REPORTING_ROUTES),
				data: { popupContainer: false },
				providers: [PopupComponent, NewDialogComponent],
			},
			{
				path: "reporting",
				outlet: "popup",
				component: PopupComponent,
				loadChildren: () => import("reports/reporting-routes").then((r) => r.REPORTING_ROUTES),
				data: { popupContainer: false },
				providers: [PopupComponent, NewDialogComponent],
			},
			{
				path: "administration",
				loadChildren: () => import("administration/administration-routes").then((r) => r.ADMINISTRATION_ROUTES),
				data: { popupContainer: false },
				providers: [PopupComponent, NewDialogComponent],
			},
			{
				path: "administration",
				outlet: "popup",
				component: PopupComponent,
				loadChildren: () => import("administration/administration-routes").then((r) => r.ADMINISTRATION_ROUTES),
				data: { popupContainer: false },
				providers: [PopupComponent, NewDialogComponent],
			},
			{
				path: "user",
				loadChildren: () => import("user/user-routes").then((r) => r.USER_ROUTES),
				data: { popupContainer: false },
			},
			{
				path: "user",
				outlet: "dialog",
				loadChildren: () => import("user/user-routes").then((r) => r.USER_ROUTES),
				data: { popupContainer: false },
			},
			{
				path: "user",
				outlet: "popup",
				component: PopupComponent,
				loadChildren: () => import("user/user-routes").then((r) => r.USER_ROUTES),
				data: { popupContainer: false },
			},
			{
				path: "search",
				outlet: "dialog",
				component: GlobalSearchResultsComponent,
				data: { popupContainer: false },
			},
			{
				path: "install",
				outlet: "popup",
				component: PopupComponent,
				loadChildren: () => import("install/install-routes").then((r) => r.INSTALL_ROUTES),
				data: { popupContainer: false },
			},
			{
				path: "ai",
				outlet: "popup",
				component: PopupComponent,
				loadChildren: () =>
					import("administration/ai/ai-administration-routes").then((r) => r.AI_ADMINISTRATION_ROUTES),
				data: { popupContainer: false },
			},
			{
				path: "files",
				children: FILES_ROUTES,
				data: { popupContainer: false },
			},
			{
				path: "files",
				outlet: "popup",
				component: PopupComponent,
				children: FILES_ROUTES,
				data: { popupContainer: false },
			},
			{
				path: "files",
				outlet: "dialog",
				children: FILES_ROUTES,
				data: { popupContainer: false },
			},
			{
				path: "print",
				outlet: "popup",
				component: PopupComponent,
				loadChildren: () => import("print/print-routes").then((r) => r.PRINT_ROUTES),
				data: { popupContainer: false },
			},
			{
				path: "tasks",
				outlet: "popup",
				component: PopupComponent,
				data: { popupContainer: false },
				children: [
					{
						component: TaskDetailsComponent,
						path: "task/:id",
						resolve: {
							task: TaskDetailsResolver,
						},
						data: { releasePreviewSetting: "ON_FOR_TARGET" },
					},
					{
						component: TaskProcessDiffRedirectComponent,
						path: "taskdiff/:id",
						resolve: {
							task: TodoResolver,
						},
					},
				],
			},
			{
				path: "error",
				component: ErrorComponent,
				data: {
					titleKey: "error.title",
				},
			},
			{
				path: "errorReport",
				component: ErrorReportComponent,
				data: {
					titleKey: "error.title",
				},
			},
		],
	},
	{
		path: "unavailable",
		component: UnavailableComponent,
		data: {
			titleKey: "error.title",
		},
	},
	{
		path: "errorMessage",
		component: ErrorMessageComponent,
		data: {
			titleKey: "error.title",
		},
	},
	{
		path: "",
		pathMatch: "full",
		component: ProxyComponent,
		canActivate: [HomeGuard],
	},
	{
		path: "**",
		redirectTo: "/",
	},
];
